import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useNavigate, useParams } from "react-router-dom";
import { pushApi } from "../../api/push";
import {
  getPanelPushSubscription,
  subscribePanelPush,
  webPushSupported,
} from "../../utils/webPushClient";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const DEFAULT_PREFERENCES = {
  storefrontActivity: true,
  commercial: true,
  operations: true,
  erp: true,
  orders: true,
  payments: true,
  billing: true,
  security: true,
  panelActivity: false,
  system: true,
  campaigns: false,
};

const PREFERENCE_OPTIONS = [
  ["storefrontActivity", "Visitas y navegación", "Incluye cada entrada al home, catálogo, producto, contacto, carrito y checkout."],
  ["commercial", "Acciones comerciales", "WhatsApp, teléfono, email, carrito y otras señales de intención."],
  ["operations", "Productos y página", "Altas, ediciones, bajas, imágenes, landing, agenda e integraciones."],
  ["erp", "ERP, caja y stock", "Movimientos operativos realizados por dueños o empleados."],
  ["orders", "Pedidos", "Nuevos pedidos y cambios relevantes de estado."],
  ["payments", "Pagos", "Pagos aprobados, rechazados o pendientes."],
  ["billing", "Suscripción de WebProfit", "Vencimientos, renovaciones, pausas y reactivaciones."],
  ["security", "Accesos y usuarios", "Cambios de usuarios, permisos y eventos de seguridad."],
  ["panelActivity", "Navegación del equipo", "Recorridos de dueños y empleados por el panel. Puede generar muchas alertas."],
  ["system", "Sistema", "Pruebas, fallos técnicos y movimientos generales."],
];

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function Metric({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}>{icon}</Box>
          <Box><Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 900 }}>{title}</Typography><Typography variant="h4" sx={{ color: BRAND.dark, fontWeight: 950 }}>{value}</Typography><Typography variant="caption" sx={{ color: "rgba(25,36,47,.50)" }}>{helper}</Typography></Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProjectNotificationsPage({ embedded = false }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [browserSubscribed, setBrowserSubscribed] = React.useState(false);
  const [preferences, setPreferences] = React.useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = React.useState(true);
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [form, setForm] = React.useState({ audience: "storefront", title: "", body: "", url: "/#/productos" });

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await pushApi.status(projectId);
      setData(response);
      if (webPushSupported()) {
        const subscription = await getPanelPushSubscription();
        const own = (response.ownSubscriptions || []).find((item) => item.endpoint === subscription?.endpoint);
        setBrowserSubscribed(Boolean(own));
        if (own?.preferences) setPreferences((current) => ({ ...current, ...own.preferences, campaigns: false }));
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar Web Push");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const saveSubscription = async (successMessage) => {
    const subscription = await subscribePanelPush(data?.publicKey);
    await pushApi.subscribe(projectId, subscription.toJSON(), preferences);
    setBrowserSubscribed(true);
    setMessage(successMessage);
    await load();
  };

  const activate = async () => {
    try {
      setWorking(true); setError(""); setMessage("");
      await saveSubscription("Este navegador ya recibe actividad y movimientos del proyecto.");
    } catch (err) {
      setError(err.message || "No se pudieron activar las notificaciones");
    } finally { setWorking(false); }
  };

  const savePreferences = async () => {
    try {
      setWorking(true); setError(""); setMessage("");
      await saveSubscription("Preferencias de alertas guardadas para este navegador.");
    } catch (err) {
      setError(err.message || "No se pudieron guardar las preferencias");
    } finally { setWorking(false); }
  };

  const deactivate = async () => {
    try {
      setWorking(true); setError(""); setMessage("");
      const subscription = await getPanelPushSubscription();
      if (subscription?.endpoint) await pushApi.unsubscribe(projectId, subscription.endpoint);
      setBrowserSubscribed(false);
      setMessage("Se desactivaron solamente las alertas de este proyecto. Las demás suscripciones del navegador siguen intactas.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudieron desactivar las notificaciones");
    } finally { setWorking(false); }
  };

  const send = async (event) => {
    event.preventDefault();
    try {
      setWorking(true); setError(""); setMessage("");
      const response = await pushApi.send(projectId, form);
      const result = response.result || {};
      setMessage(`Notificación procesada: ${result.sent || 0} enviadas, ${result.failed || 0} fallidas.`);
      setForm((current) => ({ ...current, title: "", body: "" }));
      await load();
    } catch (err) {
      setError(err.message || "No se pudo enviar la notificación");
    } finally { setWorking(false); }
  };

  const canSend = ["owner", "admin", "superadmin"].includes(data?.role);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant={embedded ? "h5" : "h3"} sx={{ fontWeight: 950, color: BRAND.dark }}>Notificaciones Web Push</Typography>
          <Typography sx={{ mt: .5, color: "rgba(25,36,47,.64)", maxWidth: 900 }}>
            El equipo recibe movimientos del negocio. Los visitantes de la web son otra audiencia independiente y solamente reciben promociones que hayan aceptado.
          </Typography>
        </Box>
        <Chip icon={browserSubscribed ? <NotificationsActiveRoundedIcon /> : <NotificationsOffRoundedIcon />} label={browserSubscribed ? "Alertas internas activas" : "Alertas internas desactivadas"} color={browserSubscribed ? "success" : "default"} sx={{ fontWeight: 900, alignSelf: "flex-start" }} />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      {!data?.enabled && !loading && <Alert severity="warning" action={<Button color="inherit" startIcon={<WorkspacePremiumRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectId}/configuracion/suscripcion?feature=Notificaciones%20Web%20Push`)} sx={{ textTransform: "none", fontWeight: 900 }}>Mejorar plan</Button>}>El módulo Web Push no está activo en este proyecto.</Alert>}
      {data?.enabled && !data?.configured && <Alert severity="warning">El módulo está activo, pero todavía faltan las claves VAPID en el backend.</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}><Metric icon={<NotificationsActiveRoundedIcon />} title="Equipo conectado" value={data?.ownerSubscribers || 0} helper="Navegadores internos" /></Grid>
        <Grid item xs={12} md={4}><Metric icon={<PeopleAltRoundedIcon />} title="Visitantes suscriptos" value={data?.storefrontSubscribers || 0} helper="Solo promociones opt-in" /></Grid>
        <Grid item xs={12} md={4}><Metric icon={<CampaignRoundedIcon />} title="Envíos manuales" value={data?.recentCampaigns?.length || 0} helper="Historial de campañas y pruebas" /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Qué movimientos querés recibir</Typography>
        <Typography sx={{ mt: .5, color: "rgba(25,36,47,.62)" }}>El historial se guarda siempre. Estos controles solamente deciden qué eventos hacen sonar una notificación en este navegador.</Typography>
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          {PREFERENCE_OPTIONS.map(([key, label, helper]) => (
            <Grid item xs={12} md={6} key={key}>
              <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: "#F6F7FB" }}>
                <FormControlLabel control={<Switch checked={preferences[key] !== false} onChange={(event) => setPreferences((current) => ({ ...current, [key]: event.target.checked }))} />} label={<Box><Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{label}</Typography><Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)" }}>{helper}</Typography></Box>} sx={{ alignItems: "flex-start", m: 0 }} />
              </Box>
            </Grid>
          ))}
        </Grid>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
          {!browserSubscribed ? (
            <Button disabled={working || !data?.enabled || !data?.configured || !webPushSupported()} onClick={activate} startIcon={<NotificationsActiveRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Activar alertas internas</Button>
          ) : (
            <>
              <Button disabled={working} onClick={savePreferences} startIcon={<SaveRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Guardar preferencias</Button>
              <Button disabled={working} onClick={deactivate} startIcon={<NotificationsOffRoundedIcon />} color="warning" variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Desactivar para este proyecto</Button>
            </>
          )}
        </Stack>
      </Paper>

      {canSend && (
        <Paper component="form" onSubmit={send} elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
          <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Enviar una notificación manual</Typography>
          <Typography sx={{ mt: .5, mb: 2, color: "rgba(25,36,47,.62)" }}>“Visitantes” envía una promoción. “Equipo” envía una prueba o aviso interno. Las audiencias no se mezclan.</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><TextField select fullWidth label="Audiencia" value={form.audience} onChange={(event) => setForm((current) => ({ ...current, audience: event.target.value }))}><MenuItem value="storefront">Visitantes suscriptos: promociones</MenuItem><MenuItem value="owner">Dueños y empleados: aviso interno</MenuItem><MenuItem value="all">Ambas audiencias, separadas</MenuItem></TextField></Grid>
            <Grid item xs={12} md={8}><TextField fullWidth label="Título" value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} inputProps={{ maxLength: 160 }} required /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Mensaje" value={form.body} onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))} inputProps={{ maxLength: 1000 }} required /></Grid>
            <Grid item xs={12} md={8}><TextField fullWidth label="URL al abrir" value={form.url} onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))} helperText="Ejemplo: /#/productos o /#/dashboard/proyectos/.../actividad" /></Grid>
            <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center" }}><Button type="submit" fullWidth disabled={working || !data?.enabled || !data?.configured} startIcon={<SendRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Enviar ahora</Button></Grid>
          </Grid>
        </Paper>
      )}

      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Envíos manuales recientes</Typography>
        <Stack spacing={1.25} sx={{ mt: 2 }}>
          {(data?.recentCampaigns || []).map((campaign) => (
            <Box key={campaign.id || campaign._id} sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}><Box><Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{campaign.title}</Typography><Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>{campaign.body}</Typography></Box><Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap><Chip size="small" label={campaign.audience} /><Chip size="small" color="success" label={`${campaign.sent || 0} enviados`} />{campaign.failed ? <Chip size="small" color="warning" label={`${campaign.failed} fallidos`} /> : null}</Stack></Stack>
              <Typography variant="caption" sx={{ mt: 1, display: "block", color: "rgba(25,36,47,.5)" }}>{dateLabel(campaign.createdAt)}</Typography>
            </Box>
          ))}
          {!data?.recentCampaigns?.length && <Alert severity="info">Todavía no hay envíos manuales registrados.</Alert>}
        </Stack>
      </Paper>
    </Stack>
  );
}
